import { z } from "zod";
import { symbolSchema } from "./instrument";
import type { Money, Price, Qty } from "./money";

/**
 * The canonical order. Every dialect — ours, Alpaca's, any future one —
 * translates to and from exactly this shape.
 */

export const ORDER_SIDES = ["buy", "sell"] as const;
export type OrderSide = (typeof ORDER_SIDES)[number];

export const ORDER_TYPES = ["market", "limit", "stop", "stop_limit", "trailing_stop"] as const;
export type OrderType = (typeof ORDER_TYPES)[number];

export const TIME_IN_FORCE = ["day", "gtc", "ioc", "fok", "opg", "cls"] as const;
export type TimeInForce = (typeof TIME_IN_FORCE)[number];

/**
 * The order lifecycle. The transition table that makes this total lives in
 * `packages/oms/src/state.ts` — this file only names the states, because
 * `packages/domain` must stay free of behavior.
 */
export const ORDER_STATUSES = [
  "pending_new",
  "new",
  "partially_filled",
  "filled",
  "pending_cancel",
  "canceled",
  "pending_replace",
  "replaced",
  "rejected",
  "expired",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Terminal states accept no further events. A fill arriving here is dropped and audited. */
export const TERMINAL_STATUSES = [
  "filled",
  "canceled",
  "replaced",
  "rejected",
  "expired",
] as const satisfies readonly OrderStatus[];

export function isTerminal(status: OrderStatus): boolean {
  return (TERMINAL_STATUSES as readonly OrderStatus[]).includes(status);
}

/** Orders in these states are live: they consume buying power and may still fill. */
export function isOpen(status: OrderStatus): boolean {
  return !isTerminal(status) && status !== "pending_new";
}

/** Only these types rest in a SymbolBookDO waiting on a price trigger. */
export function restsOnTrigger(type: OrderType): boolean {
  return type !== "market";
}

export type Order = {
  id: string;
  accountId: string;
  userId: string;
  /**
   * Caller-supplied, unique per account. THE idempotency key for the whole
   * system — see HLD-oms. Never optional: the API generates one when a
   * caller omits it, because idempotency that is sometimes-on is idempotency
   * that is off.
   */
  clientOrderId: string;
  symbol: string;
  side: OrderSide;
  qty: Qty;
  type: OrderType;
  timeInForce: TimeInForce;
  limitPrice?: Price;
  stopPrice?: Price;
  trailAmount?: Money;
  status: OrderStatus;
  filledQty: Qty;
  avgFillPrice?: Price;
  /** Set when status is `rejected`, including PolicyGate rejections. */
  rejectReason?: string;
  /** Who submitted this. Agent orders are attributable for the audit trail. */
  submittedBy: "user" | "agent" | "system";
  agentRunId?: string;
  submittedAt: Date;
  updatedAt: Date;
  filledAt?: Date;
  canceledAt?: Date;
  expiresAt?: Date;
};

// ---------------------------------------------------------------------------
// Wire schema
// ---------------------------------------------------------------------------

/**
 * Prices and quantities cross the wire as decimal STRINGS, never JSON numbers.
 *
 * `JSON.parse('{"qty": 0.1}')` yields an IEEE double before any validation can
 * intervene, so accepting a number here would reintroduce float error at the
 * one boundary the whole `money.ts` module exists to defend. Callers that send
 * a number get a validation error naming the field.
 */
const decimalString = z
  .string()
  .regex(/^-?\d+(\.\d+)?$/, "must be a decimal string, not a JSON number");

const positiveDecimalString = decimalString.refine(
  (s) => !s.startsWith("-") && /[1-9]/.test(s),
  "must be greater than zero",
);

export const newOrderRequestSchema = z
  .object({
    client_order_id: z.string().min(1).max(128).optional(),
    symbol: symbolSchema,
    side: z.enum(ORDER_SIDES),
    qty: positiveDecimalString,
    type: z.enum(ORDER_TYPES),
    time_in_force: z.enum(TIME_IN_FORCE).default("day"),
    limit_price: positiveDecimalString.optional(),
    stop_price: positiveDecimalString.optional(),
    trail_amount: positiveDecimalString.optional(),
  })
  .strict()
  .superRefine((o, ctx) => {
    const require = (field: "limit_price" | "stop_price" | "trail_amount") => {
      if (o[field] === undefined) {
        ctx.addIssue({
          code: "custom",
          path: [field],
          message: `${field} is required for a ${o.type} order`,
        });
      }
    };
    const forbid = (field: "limit_price" | "stop_price" | "trail_amount") => {
      if (o[field] !== undefined) {
        ctx.addIssue({
          code: "custom",
          path: [field],
          message: `${field} is not valid on a ${o.type} order`,
        });
      }
    };

    switch (o.type) {
      case "market":
        forbid("limit_price");
        forbid("stop_price");
        forbid("trail_amount");
        break;
      case "limit":
        require("limit_price");
        forbid("stop_price");
        forbid("trail_amount");
        break;
      case "stop":
        require("stop_price");
        forbid("limit_price");
        forbid("trail_amount");
        break;
      case "stop_limit":
        require("limit_price");
        require("stop_price");
        forbid("trail_amount");
        break;
      case "trailing_stop":
        require("trail_amount");
        forbid("limit_price");
        forbid("stop_price");
        break;
    }

    // A market order that could rest is a contradiction: it has no price to
    // rest against, so `gtc` on a market order would rest forever.
    if (o.type === "market" && (o.time_in_force === "gtc" || o.time_in_force === "opg")) {
      ctx.addIssue({
        code: "custom",
        path: ["time_in_force"],
        message: `time_in_force '${o.time_in_force}' is not valid on a market order`,
      });
    }
  });

export type NewOrderRequest = z.infer<typeof newOrderRequestSchema>;

/** Replace is cancel + new in the state machine; this is the patch that shapes the new order. */
export const replaceOrderRequestSchema = z
  .object({
    qty: positiveDecimalString.optional(),
    limit_price: positiveDecimalString.optional(),
    stop_price: positiveDecimalString.optional(),
    trail_amount: positiveDecimalString.optional(),
    time_in_force: z.enum(TIME_IN_FORCE).optional(),
    client_order_id: z.string().min(1).max(128).optional(),
  })
  .strict()
  .refine((o) => Object.keys(o).length > 0, "replace requires at least one field");

export type ReplaceOrderRequest = z.infer<typeof replaceOrderRequestSchema>;
